package org.vreo.devicehub.features.userAuthentication.data.network

import io.ktor.client.HttpClient
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.URLProtocol
import io.ktor.http.contentType
import io.ktor.http.path

import org.vreo.devicehub.features.core.data.safeCall
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.userAuthentication.data.dto.NewVerificationCodeRequest
import org.vreo.devicehub.features.userAuthentication.data.dto.UserAuthenticationDto
import org.vreo.devicehub.features.userAuthentication.data.dto.UserDetailsDto
import org.vreo.devicehub.features.userAuthentication.data.dto.UserRegistrationResponseDto
import org.vreo.devicehub.features.userAuthentication.data.dto.UserSignInRequestDto
import org.vreo.devicehub.features.userAuthentication.data.dto.VerificationDto

/**
 * Remote data source for users. Uses Ktor client to make requests to server and access user data.
 * class is responsible for making user sign-in or sign-up requests to the server.
 *
 * @param httpClient Ktor HTTP client.
 * @param hostUrl URL of the server.
 * @param signInRoute Route to sign in user.
 * @param userDetailsRoute Route for requesting user's data
 * @param registerRoute Route for registering new user
 * @param resendVerificationRoute Route for requesting new verification code for pending user
 * @param verificationRoute Route for verifying newly registered user account
 * @param urlProtocol Protocol to use for URL.
 */
class KtorRemoteUserDataSource(
    private val httpClient: HttpClient,
    private val hostUrl: String,
    private val signInRoute: String,
    private val userDetailsRoute: String,
    private val registerRoute: String,
    private val resendVerificationRoute: String,
    private val verificationRoute: String,
    private val urlProtocol: URLProtocol = URLProtocol.HTTPS
) {
    /**
     * Signs in user.
     *
     * @param request User sign-in request.
     * @return Result of user details on success or error on failure.
     */
    suspend fun signIn(
        request: UserSignInRequestDto
    ): Result<UserAuthenticationDto, DataError.Remote> {
        return safeCall<UserAuthenticationDto>(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(signInRoute)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Get user details using authentication token
     *
     * @param token JWT authentication token for authenticated user
     * @return Result of user details on success or error on failure
     */
    suspend fun getUserDetails(
        token: String
    ): Result<UserDetailsDto, DataError.Remote> {
        return safeCall<UserDetailsDto>(
            execute = {
                httpClient.get {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(userDetailsRoute)
                    }
                    contentType(ContentType.Application.Json)
                    bearerAuth(token)
                }
            }
        )
    }

    /**
     * Register new user
     *
     * @param request User registration request, new user details
     * @return Result of user registration response on success or error on failure
     */
    suspend fun registerNewUser(
        request: UserDetailsDto
    ): Result<UserRegistrationResponseDto, DataError.Remote> {
        return safeCall<UserRegistrationResponseDto>(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(registerRoute)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Request new verification code
     *
     * @param request request containing user details
     * @return Result of user registration response on success or error on failure
     */
    suspend fun requestNewVerificationCode(
        request: NewVerificationCodeRequest
    ): Result<UserRegistrationResponseDto, DataError.Remote> {
        return safeCall<UserRegistrationResponseDto>(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(resendVerificationRoute)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }

    /**
     * Verify user account
     *
     * @param request request containing verification details
     * @return Result of user authentication on success or error on failure
     */
    suspend fun verifyUser(
        request: VerificationDto
    ): Result<UserAuthenticationDto, DataError.Remote> {
        return safeCall<UserAuthenticationDto>(
            execute = {
                httpClient.post {
                    url {
                        protocol = urlProtocol
                        host = hostUrl
                        path(verificationRoute)
                    }
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
            }
        )
    }
}